import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'motion/react';
import {
  Link2,
  BarChart3,
  QrCode,
  CheckCircle,
  Loader2,
  Copy,
  Check,
  ArrowRight,
  Sparkles,
  MousePointer,
  TrendingUp,
  ExternalLink,
  Share2,
  Clock,
  Trash2,
  Church,
  UtensilsCrossed,
  HeartHandshake,
  Lightbulb,
  Target,
  Zap,
  Brain,
  Calendar,
  Megaphone,
  GraduationCap,
} from 'lucide-react';
import { toast } from 'sonner';

// ⚡ Performance Optimization: Lazy load QR code library for recent links section
const LazyQRCodeCanvas = lazy(() =>
  import('qrcode.react').then((module) => ({
    default: module.QRCodeCanvas,
  }))
);
import { accessRequestSchema, type AccessRequestInput } from '@/client/lib/validations';
import { cn } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { useAuth } from '@/client/hooks/use-auth';
import { useReducedMotion } from '@/client/hooks/use-reduced-motion';
import {
  STEALTH_MODE,
  getEnabledServices,
  getServiceNamesList,
  getEnabledServiceCount,
} from '@/client/lib/config';

// =============================================================================
// Types
// =============================================================================

interface RecentLink {
  shortUrl: string;
  shortCode: string;
  longUrl: string;
  qrCodeUrl: string;
  dateCreated: string;
  expiresAt?: string; // For public links with 90-day expiry
  clicks?: number; // Track clicks for demo
}

const STORAGE_KEY = 'arml_recent_links';
const MAX_RECENT_LINKS = 5;

// Helper to scroll to request-access section
function scrollToRequestAccess(e: React.MouseEvent) {
  e.preventDefault();
  const element = document.getElementById('request-access');
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
}

// =============================================================================
// LocalStorage helpers
// =============================================================================

function getRecentLinks(): RecentLink[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveRecentLink(link: RecentLink): RecentLink[] {
  const links = getRecentLinks();
  // Remove duplicate if exists
  const filtered = links.filter((l) => l.shortCode !== link.shortCode);
  // Add to beginning and limit to max
  const updated = [link, ...filtered].slice(0, MAX_RECENT_LINKS);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

function updateLinkClicks(shortCode: string, clicks: number): RecentLink[] {
  const links = getRecentLinks();
  const updated = links.map((l) => (l.shortCode === shortCode ? { ...l, clicks } : l));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

function removeRecentLink(shortCode: string): RecentLink[] {
  const links = getRecentLinks();
  const updated = links.filter((l) => l.shortCode !== shortCode);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  return updated;
}

// =============================================================================
// Interactive Link Creator Component with Click Counter
// =============================================================================

function UrlShortenerDemo() {
  const prefersReducedMotion = useReducedMotion();
  const [url, setUrl] = useState('');
  const [isShortening, setIsShortening] = useState(false);
  const [recentLinks, setRecentLinks] = useState<RecentLink[]>([]);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [showQrCode, setShowQrCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  // Load recent links from localStorage on mount
  useEffect(() => {
    setRecentLinks(getRecentLinks());
  }, []);

  // Periodically fetch click counts for recent links
  useEffect(() => {
    const fetchClickCounts = async () => {
      const links = getRecentLinks();
      if (links.length === 0) return;

      for (const link of links) {
        try {
          const response = await apiFetch(`/api/public/links/${link.shortCode}/clicks`);
          if (response.ok) {
            const data = await response.json();
            if (data.clicks !== link.clicks) {
              const updated = updateLinkClicks(link.shortCode, data.clicks);
              setRecentLinks(updated);
            }
          }
        } catch {
          // Silently fail - click counts are nice-to-have
        }
      }
    };

    // Fetch immediately, then every 10 seconds
    fetchClickCounts();
    const interval = setInterval(fetchClickCounts, 10000);
    return () => clearInterval(interval);
  }, [recentLinks.length]);

  const handleShorten = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!url.trim()) {
      toast.error('Please enter a URL to shorten');
      return;
    }

    setIsShortening(true);
    setError(null);

    try {
      // Use authenticated endpoint if logged in, otherwise public endpoint
      const endpoint = isAuthenticated ? '/api/links' : '/api/public/shorten';
      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create short link');
      }

      // Map API response to RecentLink interface
      // Both endpoints return similar structure with shortCode, shortUrl, destinationUrl
      const recentLink: RecentLink = {
        shortCode: data.shortCode,
        shortUrl: data.shortUrl,
        longUrl: data.destinationUrl,
        qrCodeUrl: `${data.shortUrl}/qr`,
        dateCreated: new Date().toISOString(),
        expiresAt: data.expiresAt || null, // Authenticated links don't expire
        clicks: 0,
      };

      // Save to localStorage for display (both auth and non-auth users see recent links)
      const updated = saveRecentLink(recentLink);
      setRecentLinks(updated);
      setUrl('');

      if (isAuthenticated) {
        // Invalidate links cache so dashboard sees the new link
        queryClient.invalidateQueries({ queryKey: ['links'] });
        toast.success('Link created and saved to your account!');
      } else {
        toast.success('Link shortened! Sign up to save permanently.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create short link';
      setError(message);
      toast.error(message);
    } finally {
      setIsShortening(false);
    }
  };

  const handleCopy = useCallback(async (shortUrl: string, shortCode: string) => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopiedCode(shortCode);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  }, []);

  const handleShare = useCallback(async (shortUrl: string, _longUrl: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out this link',
          url: shortUrl,
        });
      } catch {
        // User cancelled or share failed silently
      }
    } else {
      // Fallback: copy to clipboard
      await navigator.clipboard.writeText(shortUrl);
      toast.success('Link copied for sharing!');
    }
  }, []);

  const handleRemove = useCallback(
    (shortCode: string) => {
      // If not in confirm mode, show confirmation
      if (confirmDelete !== shortCode) {
        setConfirmDelete(shortCode);
        // Auto-reset after 3 seconds
        setTimeout(() => setConfirmDelete((prev) => (prev === shortCode ? null : prev)), 3000);
        return;
      }
      // If already confirmed, delete
      const updated = removeRecentLink(shortCode);
      setRecentLinks(updated);
      setConfirmDelete(null);
      toast.success('Link removed from history');
    },
    [confirmDelete]
  );

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const truncateUrl = (url: string, maxLength = 40) => {
    if (url.length <= maxLength) return url;
    return `${url.substring(0, maxLength - 3)}…`;
  };

  const formatExpiryDays = (expiresAt?: string) => {
    if (!expiresAt) return null;
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return 'Expired';
    if (diffDays === 1) return '1 day left';
    return `${diffDays} days left`;
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: prefersReducedMotion ? 0 : 0.4,
          duration: prefersReducedMotion ? 0.01 : 0.6,
        }}
        className="relative"
      >
        {/* Decorative border - subtle glow using theme variable */}
        <div className="absolute -inset-[1px] bg-[var(--color-glow-primary)] rounded-2xl blur-lg" />

        <div className="relative bg-card rounded-2xl p-6 md:p-8 border border-border shadow-md">
          {/* URL Input Form */}
          <form onSubmit={handleShorten} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <input
                  type="url"
                  name="url"
                  autoComplete="url"
                  inputMode="url"
                  spellCheck={false}
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setError(null);
                  }}
                  placeholder="Paste any URL (e.g., google.com)"
                  className={cn(
                    'w-full h-14 px-5 pr-12 bg-muted/50 border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 transition-all text-lg',
                    error
                      ? 'border-destructive/50 focus:ring-destructive/50 focus:border-destructive/50'
                      : 'border-border focus:ring-primary/50 focus:border-primary/50'
                  )}
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <Link2 className="w-5 h-5" />
                </div>
              </div>

              <motion.button
                type="submit"
                disabled={isShortening}
                whileHover={{ scale: prefersReducedMotion ? 1 : 1.02 }}
                whileTap={{ scale: prefersReducedMotion ? 1 : 0.98 }}
                className="h-14 px-8 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:opacity-70 text-zinc-900 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 min-w-[140px] shadow-lg shadow-amber-500/25"
              >
                {isShortening ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Creating…</span>
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" aria-hidden="true" />
                    <span>Create</span>
                  </>
                )}
              </motion.button>
            </div>

            {error && (
              <motion.p
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: prefersReducedMotion ? 0.01 : 0.3 }}
                className="text-sm text-destructive"
              >
                {error}
              </motion.p>
            )}
          </form>

          {/* Recent Links with Click Counter */}
          <AnimatePresence>
            {recentLinks.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: prefersReducedMotion ? 'auto' : 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: prefersReducedMotion ? 'auto' : 0 }}
                transition={{ duration: prefersReducedMotion ? 0.01 : 0.3 }}
                className="overflow-hidden"
              >
                <div className="pt-6 mt-6 border-t border-border">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Clock className="w-4 h-4" aria-hidden="true" />
                      Your Links
                    </h3>
                    <span className="text-xs text-muted-foreground/70">
                      Click count updates live
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground/60 mb-4">
                    Visit your link in another tab to see the click count increase.{' '}
                    <button
                      onClick={scrollToRequestAccess}
                      className="text-primary/70 hover:text-primary underline underline-offset-2"
                    >
                      Request access
                    </button>{' '}
                    for detailed analytics.
                  </p>

                  <div className="space-y-3">
                    {recentLinks.map((link, index) => (
                      <motion.div
                        key={link.shortCode}
                        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: prefersReducedMotion ? 0 : -20 }}
                        transition={{
                          delay: prefersReducedMotion ? 0 : index * 0.05,
                          duration: prefersReducedMotion ? 0.01 : 0.3,
                        }}
                        className="group relative p-4 bg-muted/50 hover:bg-muted/70 rounded-xl border border-border/50 hover:border-border transition-all"
                      >
                        <div
                          className="flex flex-col sm:flex-row sm:items-center gap-3 cursor-pointer"
                          onClick={() => showQrCode === link.shortCode && setShowQrCode(null)}
                        >
                          {/* Link info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="font-mono text-primary font-medium truncate">
                                {link.shortUrl.replace('https://', '')}
                              </p>
                              {/* Click counter badge */}
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary shrink-0">
                                <MousePointer className="w-3 h-3" aria-hidden="true" />
                                {link.clicks ?? 0} {(link.clicks ?? 0) === 1 ? 'click' : 'clicks'}
                              </span>
                            </div>
                            <p
                              className="text-sm text-muted-foreground truncate mt-0.5"
                              title={link.longUrl}
                            >
                              {truncateUrl(link.longUrl)}
                            </p>
                          </div>

                          {/* Time and Expiry */}
                          <div className="flex flex-col items-end gap-0.5 shrink-0 hidden sm:flex">
                            <span className="text-xs text-muted-foreground/60">
                              {formatTimeAgo(link.dateCreated)}
                            </span>
                            {link.expiresAt && (
                              <span className="text-xs text-primary/70">
                                {formatExpiryDays(link.expiresAt)}
                              </span>
                            )}
                          </div>

                          {/* Actions */}
                          <div
                            className="flex items-center gap-2 shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Visit */}
                            <a
                              href={link.shortUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg bg-foreground/5 hover:bg-foreground/10 text-muted-foreground hover:text-foreground transition-all"
                              title="Visit link (adds to click count!)"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>

                            {/* QR Code */}
                            <button
                              onClick={() =>
                                setShowQrCode(showQrCode === link.shortCode ? null : link.shortCode)
                              }
                              className={cn(
                                'p-2 rounded-lg transition-all',
                                showQrCode === link.shortCode
                                  ? 'bg-primary/20 text-primary'
                                  : 'bg-foreground/5 hover:bg-foreground/10 text-muted-foreground hover:text-foreground'
                              )}
                              title={
                                showQrCode === link.shortCode ? 'Hide QR code' : 'Show QR code'
                              }
                            >
                              <QrCode className="w-4 h-4" />
                            </button>

                            {/* Share */}
                            <button
                              onClick={() => handleShare(link.shortUrl, link.longUrl)}
                              className="p-2 rounded-lg bg-foreground/5 hover:bg-foreground/10 text-muted-foreground hover:text-foreground transition-all"
                              title="Share link"
                            >
                              <Share2 className="w-4 h-4" />
                            </button>

                            {/* Copy */}
                            <button
                              onClick={() => handleCopy(link.shortUrl, link.shortCode)}
                              className={cn(
                                'p-2 rounded-lg transition-all',
                                copiedCode === link.shortCode
                                  ? 'bg-green-500/20 text-green-400'
                                  : 'bg-foreground/5 hover:bg-foreground/10 text-muted-foreground hover:text-foreground'
                              )}
                              title="Copy link"
                            >
                              {copiedCode === link.shortCode ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <Copy className="w-4 h-4" />
                              )}
                            </button>

                            {/* Delete */}
                            {confirmDelete === link.shortCode ? (
                              <button
                                onClick={() => handleRemove(link.shortCode)}
                                className="px-3 py-1.5 rounded-lg bg-red-500/30 text-red-400 text-xs font-medium animate-pulse hover:bg-red-500/40 transition-all whitespace-nowrap"
                              >
                                Confirm delete?
                              </button>
                            ) : (
                              <button
                                onClick={() => handleRemove(link.shortCode)}
                                className="p-2 rounded-lg bg-foreground/5 hover:bg-red-500/20 text-muted-foreground hover:text-red-400 transition-all"
                                title="Delete link"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* QR Code panel */}
                        <AnimatePresence>
                          {showQrCode === link.shortCode && (
                            <motion.div
                              initial={{ opacity: 0, height: prefersReducedMotion ? 'auto' : 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: prefersReducedMotion ? 'auto' : 0 }}
                              transition={{ duration: prefersReducedMotion ? 0.01 : 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="mt-4 pt-4 border-t border-border/50 flex flex-col items-center">
                                <div className="bg-white p-3 rounded-xl">
                                  <Suspense
                                    fallback={
                                      <div
                                        className="bg-muted animate-pulse rounded"
                                        style={{ width: 128, height: 128 }}
                                      />
                                    }
                                  >
                                    <LazyQRCodeCanvas
                                      value={link.shortUrl}
                                      size={256}
                                      level="H"
                                      marginSize={1}
                                      imageSettings={{
                                        src: '/assets/logo.png',
                                        height: 56,
                                        width: 56,
                                        excavate: true,
                                      }}
                                      style={{ width: 128, height: 128 }}
                                    />
                                  </Suspense>
                                </div>
                                <p className="mt-2 text-xs text-muted-foreground">
                                  Scan to visit {link.shortUrl.replace('https://', '')}
                                </p>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* First-time prompt */}
          {recentLinks.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                delay: prefersReducedMotion ? 0 : 0.6,
                duration: prefersReducedMotion ? 0.01 : 0.3,
              }}
              className="mt-4 text-center space-y-1"
            >
              <p className="text-sm text-muted-foreground">
                Create short links instantly. No sign up required.
              </p>
              <p className="text-xs text-muted-foreground/60">
                Public links expire after 90 days.{' '}
                <button
                  onClick={scrollToRequestAccess}
                  className="text-primary/80 hover:text-primary underline underline-offset-2"
                >
                  Request access
                </button>{' '}
                for permanent links, custom slugs, and detailed analytics.
              </p>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// =============================================================================
// Insight Preview Hero - Rotating outcome cards
// =============================================================================

function InsightPreviewHero() {
  const prefersReducedMotion = useReducedMotion();
  const [activeCard, setActiveCard] = useState(0);
  // Illustrative examples showing what organizations could learn
  const insights = [
    {
      org: 'Your Church',
      icon: Church,
      metric: 'Know what works',
      insight: 'See if email or Facebook reaches more parishioners',
      color: 'from-violet-500 to-purple-600',
    },
    {
      org: 'Your Restaurant',
      icon: UtensilsCrossed,
      metric: 'Track every scan',
      insight: 'See which QR codes bring in new customers',
      color: 'from-amber-500 to-orange-600',
    },
    {
      org: 'Your Nonprofit',
      icon: HeartHandshake,
      metric: 'Find the best time',
      insight: 'Learn when your supporters are most active',
      color: 'from-rose-500 to-pink-600',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveCard((prev) => (prev + 1) % insights.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [insights.length]);

  return (
    <motion.div
      initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay: prefersReducedMotion ? 0 : 0.5,
        duration: prefersReducedMotion ? 0.01 : 0.6,
      }}
      className="relative max-w-md mx-auto"
    >
      {/* Glow effect */}
      <div className="absolute -inset-4 bg-[var(--color-glow-primary)] rounded-3xl blur-2xl" />

      <div className="relative bg-card rounded-2xl border border-border overflow-hidden shadow-lg">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
              <Brain className="w-4 h-4 text-zinc-900" />
            </div>
            <span className="font-medium text-foreground">Marketing Intelligence</span>
          </div>
        </div>

        {/* Rotating cards */}
        <div className="p-5">
          <AnimatePresence mode="wait">
            {insights.map((item, index) =>
              index === activeCard ? (
                <motion.div
                  key={item.org}
                  initial={{ opacity: 0, x: prefersReducedMotion ? 0 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: prefersReducedMotion ? 0 : -20 }}
                  transition={{ duration: prefersReducedMotion ? 0.01 : 0.3 }}
                  className="flex items-start gap-4"
                >
                  <div
                    className={cn(
                      'w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br shrink-0',
                      item.color
                    )}
                  >
                    <item.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-muted-foreground">{item.org}</p>
                    <p className="text-xl font-bold text-foreground">{item.metric}</p>
                    <p className="text-sm text-muted-foreground mt-1">{item.insight}</p>
                  </div>
                </motion.div>
              ) : null
            )}
          </AnimatePresence>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mt-5">
            {insights.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveCard(index)}
                className={cn(
                  'w-2 h-2 rounded-full transition-all',
                  index === activeCard
                    ? 'w-6 bg-gradient-to-r from-amber-500 to-orange-500'
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// =============================================================================
// Platform Tools Section (Simplified - no redundant flow)
// =============================================================================

function PlatformToolsSection() {
  const prefersReducedMotion = useReducedMotion();
  // Get enabled services from central configuration
  const enabledServices = getEnabledServices();
  const serviceCount = enabledServices.length;

  // Determine grid columns based on service count
  const gridCols =
    serviceCount <= 2
      ? 'md:grid-cols-2'
      : serviceCount <= 3
        ? 'md:grid-cols-3'
        : serviceCount <= 4
          ? 'md:grid-cols-2 lg:grid-cols-4'
          : 'md:grid-cols-2 lg:grid-cols-3';

  return (
    <section className="relative py-20 lg:py-28 bg-gradient-to-b from-muted/30 to-background border-y border-border/50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
            <Zap className="w-4 h-4" aria-hidden="true" />
            {serviceCount === 1
              ? 'One Tool, Complete Intelligence'
              : `One Platform, ${serviceCount > 2 ? 'Complete' : 'Unified'} Intelligence`}
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {serviceCount === 1 ? (
              <>
                A tool that{' '}
                <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                  thinks with you
                </span>
              </>
            ) : (
              <>
                Tools that{' '}
                <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                  think with you
                </span>
              </>
            )}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {serviceCount === 1
              ? "Every click is captured. See what's working across your marketing."
              : serviceCount === 2
                ? "Both tools capture data. Together, they reveal what's working across your marketing."
                : "Every tool captures data. Together, they reveal what's working across your entire marketing."}
          </p>
        </motion.div>

        {/* Tools Grid */}
        <div className={`grid ${gridCols} gap-6 max-w-6xl mx-auto`}>
          {enabledServices.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: prefersReducedMotion ? 0 : index * 0.1,
                duration: prefersReducedMotion ? 0.01 : 0.5,
              }}
              className="group relative bg-card rounded-2xl p-6 border border-border hover:border-primary/30 transition-all hover:shadow-lg hover:shadow-primary/5"
            >
              {/* Icon */}
              <div
                className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}
              >
                <service.icon className="w-7 h-7 text-white" aria-hidden="true" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-foreground mb-2">{service.name}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                {service.description}
              </p>

              {/* Availability badge */}
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium',
                  service.available
                    ? 'bg-primary/10 text-primary'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {service.available && (
                  <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                )}
                {service.available ? 'Available now' : 'Coming soon'}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// Intelligence Flow Section - The core value proposition
// =============================================================================

function IntelligenceFlowSection() {
  const prefersReducedMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState(0);
  const tabs = [
    { id: 'church', label: 'Church', icon: Church, color: 'from-violet-500 to-purple-600' },
    {
      id: 'restaurant',
      label: 'Restaurant',
      icon: UtensilsCrossed,
      color: 'from-amber-500 to-orange-600',
    },
    {
      id: 'nonprofit',
      label: 'Nonprofit',
      icon: HeartHandshake,
      color: 'from-rose-500 to-pink-600',
    },
    { id: 'events', label: 'Events', icon: Calendar, color: 'from-blue-500 to-cyan-600' },
    { id: 'advocacy', label: 'Advocacy', icon: Megaphone, color: 'from-red-500 to-orange-600' },
    {
      id: 'students',
      label: 'Students',
      icon: GraduationCap,
      color: 'from-emerald-500 to-teal-600',
    },
  ];

  // Illustrative scenarios showing the intelligence flow
  const scenarios = [
    {
      question: 'Which announcement reaches more people?',
      flow: [
        {
          step: 'DATA',
          text: 'Every click on your church bulletin link is captured',
          color: 'text-blue-500',
        },
        {
          step: 'INSIGHT',
          text: 'You discover 70% of clicks come from email, not Facebook',
          color: 'text-amber-500',
        },
        {
          step: 'ACTION',
          text: "You focus next month's outreach on email",
          color: 'text-orange-500',
        },
        {
          step: 'RESULT',
          text: 'More RSVPs, less time wasted on low-performing channels',
          color: 'text-emerald-500',
        },
      ],
    },
    {
      question: 'Are the table QR codes worth printing?',
      flow: [
        {
          step: 'DATA',
          text: 'QR scans show which tables and times get the most use',
          color: 'text-blue-500',
        },
        {
          step: 'INSIGHT',
          text: 'Weekend brunch tables drive 80% of menu views',
          color: 'text-amber-500',
        },
        {
          step: 'ACTION',
          text: 'You add QR codes to takeout bags for weekday customers',
          color: 'text-orange-500',
        },
        {
          step: 'RESULT',
          text: 'Online ordering increases without extra ad spend',
          color: 'text-emerald-500',
        },
      ],
    },
    {
      question: 'When should we send the donation appeal?',
      flow: [
        {
          step: 'DATA',
          text: 'Click timestamps reveal when your supporters are active',
          color: 'text-blue-500',
        },
        {
          step: 'INSIGHT',
          text: 'Tuesday evenings see 3x more engagement than weekends',
          color: 'text-amber-500',
        },
        {
          step: 'ACTION',
          text: 'You schedule your year-end appeal for Tuesday 7pm',
          color: 'text-orange-500',
        },
        {
          step: 'RESULT',
          text: 'Same email, same audience, better response rate',
          color: 'text-emerald-500',
        },
      ],
    },
    {
      question: 'Which promotion fills the venue faster?',
      flow: [
        {
          step: 'DATA',
          text: 'Track registrations from Instagram, flyers, and email blasts',
          color: 'text-blue-500',
        },
        {
          step: 'INSIGHT',
          text: 'Flyers at Armenian markets bring 4x more signups than social media',
          color: 'text-amber-500',
        },
        {
          step: 'ACTION',
          text: 'You print more flyers and distribute at community hubs',
          color: 'text-orange-500',
        },
        {
          step: 'RESULT',
          text: 'Event sells out faster with lower promotion costs',
          color: 'text-emerald-500',
        },
      ],
    },
    {
      question: 'Which districts are most engaged?',
      flow: [
        {
          step: 'DATA',
          text: 'Action alert clicks are tagged by congressional district',
          color: 'text-blue-500',
        },
        {
          step: 'INSIGHT',
          text: 'CA-30 and NJ-9 show 5x higher click rates than average',
          color: 'text-amber-500',
        },
        {
          step: 'ACTION',
          text: 'You focus volunteer phone banks on high-engagement areas',
          color: 'text-orange-500',
        },
        {
          step: 'RESULT',
          text: 'More constituent calls with the same volunteer hours',
          color: 'text-emerald-500',
        },
      ],
    },
    {
      question: 'Which platform do students actually use?',
      flow: [
        {
          step: 'DATA',
          text: 'Compare clicks from GroupMe, Instagram, and email lists',
          color: 'text-blue-500',
        },
        {
          step: 'INSIGHT',
          text: 'Instagram stories drive 60% of event signups',
          color: 'text-amber-500',
        },
        {
          step: 'ACTION',
          text: 'You prioritize story posts over email for upcoming mixer',
          color: 'text-orange-500',
        },
        {
          step: 'RESULT',
          text: 'Highest attendance at a club event this semester',
          color: 'text-emerald-500',
        },
      ],
    },
  ];

  const scenario = scenarios[activeTab];
  const currentTab = tabs[activeTab];
  const TabIcon = currentTab?.icon || Church;

  return (
    <section className="relative py-20 lg:py-28 bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            From clicks to{' '}
            <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
              clarity
            </span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See how Armenian organizations turn everyday links into marketing intelligence.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Tabs - wraps on mobile */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {tabs.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(index)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all text-sm',
                  activeTab === index
                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-zinc-900 shadow-lg shadow-amber-500/25'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: prefersReducedMotion ? 0 : -10 }}
              transition={{ duration: prefersReducedMotion ? 0.01 : 0.3 }}
            >
              {/* Scenario Card */}
              <div className="bg-card/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 border border-border/50 max-w-3xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div
                    className={cn(
                      'w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br',
                      currentTab?.color || 'from-amber-500 to-orange-600'
                    )}
                  >
                    <TabIcon className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-lg font-semibold text-foreground">
                    &quot;{scenario?.question}&quot;
                  </p>
                </div>

                {/* Intelligence Flow */}
                <div className="space-y-3">
                  {scenario?.flow.map((item, index) => (
                    <motion.div
                      key={item.step}
                      initial={{ opacity: 0, x: prefersReducedMotion ? 0 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        delay: prefersReducedMotion ? 0 : index * 0.1,
                        duration: prefersReducedMotion ? 0.01 : 0.3,
                      }}
                      className={cn(
                        'flex items-start gap-3 p-4 rounded-xl',
                        item.step === 'RESULT'
                          ? 'bg-emerald-500/10 border border-emerald-500/20'
                          : 'bg-muted/50'
                      )}
                    >
                      <div className={cn('flex items-center gap-2 shrink-0 w-20', item.color)}>
                        {item.step === 'DATA' && <BarChart3 className="w-4 h-4" />}
                        {item.step === 'INSIGHT' && <Lightbulb className="w-4 h-4" />}
                        {item.step === 'ACTION' && <Target className="w-4 h-4" />}
                        {item.step === 'RESULT' && <TrendingUp className="w-4 h-4" />}
                        <span className="text-xs font-bold tracking-wider">{item.step}</span>
                      </div>
                      <p
                        className={cn(
                          'text-sm',
                          item.step === 'RESULT'
                            ? 'text-emerald-600 font-medium'
                            : 'text-foreground/80'
                        )}
                      >
                        {item.text}
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// Main Landing Page
// =============================================================================

export function LandingPage() {
  const prefersReducedMotion = useReducedMotion();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<AccessRequestInput>({
    resolver: zodResolver(accessRequestSchema),
    defaultValues: {
      email: '',
      name: '',
      reason: '',
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: AccessRequestInput) => {
      const response = await apiFetch('/api/access-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to submit request');
      }

      return response.json();
    },
    onSuccess: () => {
      setSubmitted(true);
      toast.success('Request submitted! Check your email for confirmation.');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  return (
    <div className="bg-background min-h-screen">
      {/* Decorative background - uses theme-aware CSS variables */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orbs - subtle in light mode, visible in dark mode */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[var(--color-glow-primary)] rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/4 w-80 h-80 bg-[var(--color-glow-accent)] rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/3 w-72 h-72 bg-[var(--color-glow-purple)] rounded-full blur-3xl" />
      </div>

      {/* Section 1: Value-First Hero */}
      <section className="relative pt-20 pb-16 lg:pt-28 lg:pb-24 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left: Copy */}
            <div className="text-center lg:text-left">
              {/* Beta badge */}
              <motion.div
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: prefersReducedMotion ? 0.01 : 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                Limited Access — Request an Invite
              </motion.div>

              {/* Value-First Headline */}
              <motion.h1
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: prefersReducedMotion ? 0 : 0.1,
                  duration: prefersReducedMotion ? 0.01 : 0.6,
                }}
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6"
              >
                <span className="text-foreground">Marketing intelligence</span>
                <br />
                <span className="bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
                  for Armenian organizations.
                </span>
              </motion.h1>

              {/* Outcome-focused subheadline */}
              <motion.p
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: prefersReducedMotion ? 0 : 0.2,
                  duration: prefersReducedMotion ? 0.01 : 0.6,
                }}
                className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed"
              >
                {getEnabledServiceCount() === 1
                  ? "Every click is tracked. See who's engaging and where they came from."
                  : getEnabledServiceCount() === 2
                    ? `${getServiceNamesList()} — both capture data that tells you what's working and what's not.`
                    : `${getServiceNamesList()} — each one captures data. Together, they tell you what's working and what's not.`}
              </motion.p>

              {/* CTA buttons */}
              <motion.div
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  delay: prefersReducedMotion ? 0 : 0.3,
                  duration: prefersReducedMotion ? 0.01 : 0.6,
                }}
                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4"
              >
                <button
                  onClick={scrollToRequestAccess}
                  className="group inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 px-8 py-4 text-lg font-semibold text-zinc-900 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all hover:scale-105"
                >
                  Request Early Access
                  <ArrowRight
                    className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                    aria-hidden="true"
                  />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('try-demo')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center justify-center rounded-xl border border-border bg-muted/50 hover:bg-muted px-8 py-4 text-lg font-medium text-foreground transition-all"
                >
                  Try the Demo
                </button>
              </motion.div>
            </div>

            {/* Right: Insight Preview */}
            <InsightPreviewHero />
          </div>
        </div>
      </section>

      {/* Section 2: Platform Tools - Hidden in stealth mode */}
      {!STEALTH_MODE && <PlatformToolsSection />}

      {/* Section 3: Intelligence Flow - Hidden in stealth mode */}
      {!STEALTH_MODE && <IntelligenceFlowSection />}

      {/* Section 4: Interactive Demo */}
      <section
        id="try-demo"
        className="relative py-20 lg:py-28 bg-gradient-to-b from-muted/30 to-background"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              See it{' '}
              <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">
                in action
              </span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Create a link, then visit it in another tab. Watch the click counter update in real
              time.
            </p>
          </motion.div>

          <UrlShortenerDemo />
        </div>
      </section>

      {/* Section 5: Community Mission - Hidden in stealth mode */}
      {!STEALTH_MODE && (
        <section className="relative py-20 lg:py-28">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
                className="bg-card rounded-2xl p-8 md:p-10 border border-border shadow-sm text-center"
              >
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                  The Big Players Have Data. So Should We.
                </h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  When a Fortune 500 company shares a link, they know everything — who clicked, from
                  where, which campaign worked. Armenian churches, restaurants, and nonprofits
                  deserve the same intelligence. That's why we built Armenian Link.
                </p>
                <div className="flex flex-wrap justify-center gap-4 text-sm">
                  <span className="px-4 py-2 rounded-full bg-muted/50 border border-border text-muted-foreground">
                    From $5/month, not $300/year
                  </span>
                  <span className="px-4 py-2 rounded-full bg-muted/50 border border-border text-muted-foreground">
                    Built by Armenians, for Armenians
                  </span>
                  <span className="px-4 py-2 rounded-full bg-muted/50 border border-border text-muted-foreground">
                    Indie project, not VC-backed
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Request Access Section */}
      <section
        id="request-access"
        className="relative py-24 lg:py-32 bg-gradient-to-b from-background to-muted/30"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: prefersReducedMotion ? 0.01 : 0.6 }}
            className="max-w-xl mx-auto"
          >
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Request Access
              </h2>
              <p className="text-lg text-muted-foreground">
                We're currently invite-only. Request access and we'll get back to you within 24
                hours.
              </p>
            </div>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: prefersReducedMotion ? 1 : 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: prefersReducedMotion ? 0.01 : 0.3 }}
                className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-8 text-center"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-white" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-bold text-foreground mb-3">Request Submitted!</h3>
                <p className="text-muted-foreground">
                  We'll review your request and get back to you within 24 hours. Check your email
                  for a confirmation.
                </p>
              </motion.div>
            ) : (
              <div className="relative">
                {/* Form glow */}
                <div className="absolute -inset-[1px] bg-gradient-to-r from-amber-500/15 via-orange-500/15 to-red-500/15 rounded-2xl blur-sm" />

                <form
                  onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
                  className="relative bg-card backdrop-blur-xl rounded-2xl p-8 border border-border space-y-6 shadow-lg"
                >
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-foreground/80 mb-2"
                    >
                      Email address
                    </label>
                    <input
                      type="email"
                      id="email"
                      autoComplete="email"
                      inputMode="email"
                      spellCheck={false}
                      {...form.register('email')}
                      className="w-full h-12 px-4 bg-muted/50 border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="you@example.com"
                    />
                    {form.formState.errors.email && (
                      <p className="text-sm text-destructive mt-2">
                        {form.formState.errors.email.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-foreground/80 mb-2"
                    >
                      Your name
                    </label>
                    <input
                      type="text"
                      id="name"
                      autoComplete="name"
                      spellCheck={true}
                      {...form.register('name')}
                      className="w-full h-12 px-4 bg-muted/50 border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="John Doe"
                    />
                    {form.formState.errors.name && (
                      <p className="text-sm text-destructive mt-2">
                        {form.formState.errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="reason"
                      className="block text-sm font-medium text-foreground/80 mb-2"
                    >
                      How will you use Armenian Link?{' '}
                      <span className="text-muted-foreground">(optional)</span>
                    </label>
                    <textarea
                      id="reason"
                      autoComplete="off"
                      spellCheck={true}
                      {...form.register('reason')}
                      rows={3}
                      className="w-full px-4 py-3 bg-muted/50 border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all resize-none"
                      placeholder="I'm building a marketing campaign and need…"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={mutation.isPending}
                    className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:from-zinc-600 disabled:to-zinc-600 text-zinc-900 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25"
                  >
                    {mutation.isPending ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        Request Access
                        <ArrowRight className="w-5 h-5" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
